import React from 'react'
import PropTypes from 'prop-types'
import cn from 'classnames'
import moment from 'moment'
import { get, sumBy } from 'lodash'
import { resolvePaymentIcon } from '../../utils/icons'
import { ReactComponent as UnsyncableIcon } from '../../assets/icons/unfilled-orange-cloud.svg'
import { ReactComponent as ErrorIcon } from '../../assets/icons/error.svg'
import { ReactComponent as CompletedIcon } from '../../assets/icons/completed-icon.svg'
import { ReactComponent as ApprovedOfflineIcon } from '../../assets/icons/offline-approved-icon.svg'
import Button from '../Button'

import styles from './OrderRow.module.scss'

const OrderRow = ({ isSelected, refundable, onSelect, order, className }) => {

  const authFailed = order?.isDeclined || order?.orderState === 'failed'
  const errorClass = authFailed ? styles.error : null
  const redButtonClass =  authFailed ? styles.errorButton : null
  const unsyncableClass = order.unsyncable ? styles.unsyncable : null
  const orangeButtonClass =  order.unsyncable ? styles.orangeButton : null
  const offlineOrderClass = (order?.createOrderParams?.state === 'approved_offline') || (!order.syncedAt) ? styles.offlineOrder : null
  const classNames = cn(styles.orderRow, errorClass, unsyncableClass, offlineOrderClass, className)
  const issuer = order?.createOrderParams?.userAttributes?.freedompayIssuerName 

  const totalDisplay = (order) => {
    if (order.total) return `$${order.total}`
    if (order.amountInCents) return `$${(order.amountInCents / 100).toFixed(2)}`

    const totalInCents = sumBy(order.itemModels, 'priceInCents')
    return `$${(totalInCents / 100).toFixed(2)}`
  }

  const displayPayment = (order) => {
    
    const paymentType = order?.createOrderParams?.payments?.[0]?.paymentType ?? ''
    const isRichCheckoutOrder = order?.isRichCheckout ?? false
    const isMultiPayment = order?.isMultiPayment ?? false
    const lastFour = order?.createOrderParams?.userAttributes?.last_4

    const hasCreditCardInfo = issuer && lastFour

    let tenderDisplay
    if (paymentType === 'cash') {
      tenderDisplay = "Cash" 
    } else if (isMultiPayment) {
      tenderDisplay = "Split"
    } else if (hasCreditCardInfo) {
      tenderDisplay = `*${lastFour}`
    } else if (isRichCheckoutOrder) {
      tenderDisplay = "App"
    } else {
      tenderDisplay = "Custom"
    }

    return tenderDisplay
        
  }

  const isCardPayment = displayPayment(order).charAt(0) === "*"
  const icon = React.cloneElement(resolvePaymentIcon(isCardPayment ? issuer :  displayPayment(order)), { className: styles.icon })

  const displayCreated = (created) => {
    const date = moment(created).format('M/D/YY')
    const time = moment(created).format('h:mm a')

    return date + ", " + time
  }

  const statusDisplay = (status) => {

    switch (status) {
      case 'unsyncable': 
        return (
          <span >
            <UnsyncableIcon className={styles.statusIcon} /> Unsyncable
          </span> 
        )
      case 'authorization failed': 
        return (
          <span>
            <ErrorIcon className={styles.statusIcon} /> Authorization Failed
          </span>
        )
      case 'submission failed':
        return (
          <span>
            <ErrorIcon className={styles.statusIcon} /> Submission Failed
          </span>
        )
      case 'submitted':
        return (
          <span>
            <UnsyncableIcon className={styles.statusIcon} /> Submitted
          </span>
        )
      case 'completed':
        return (
          <span >
            <CompletedIcon className={styles.statusIcon} /> Completed
          </span>
        )
      case 'not synced':
        return (
          <span>
            <ApprovedOfflineIcon className={styles.statusIcon} /> Approved Offline
          </span>
        )
      case 'approved offline' :
        return (
          <span >
            <ApprovedOfflineIcon className={styles.statusIcon} /> Approved Offline
          </span>
        )
      default: return 'unknown'
    }
  }
  const orderNumber = (order) => get(order, 'orderNumber', '-')

  // TODO(mkramerl): Make this dynamic so text value can reflec the sync button being pressed in the detail.
  const isApprovedOffline = order?.createOrderParams?.state === 'approved_offline'
  let initialSyncStatusText = ''
  if(order.unsyncable) {
    initialSyncStatusText = statusDisplay('unsyncable')
  } else if(order.isDeclined) {
    initialSyncStatusText = statusDisplay('authorization failed')
  } else if(order.orderState === 'failed') {
    initialSyncStatusText = statusDisplay('submission failed')
  } else if(order.orderState === 'preauth') {
    initialSyncStatusText = statusDisplay('submitted')
  } else if(isApprovedOffline || !order.syncedAt) {
    initialSyncStatusText = statusDisplay('approved offline')
  } else if(order.isSyncing) {
    initialSyncStatusText = 'syncing'
  } else {
    initialSyncStatusText = statusDisplay('completed')
  }

  const mediaQueryMobile = window?.matchMedia("(max-width: 500px)")
  const mobileText = mediaQueryMobile?.matches ? ' • ' : ' '

  return (
    <div className={classNames} onClick={() => onSelect(order.uuid)}>
      <div className={styles.centerContainer}>{icon}<span className={styles.tenderName}>{displayPayment(order)}</span></div>
      <div>{mobileText}{totalDisplay(order)}</div>
      <div>{orderNumber(order)}</div>
      <div>{displayCreated(order.submittedAt)}</div>
      <div>{initialSyncStatusText}</div>
      <div>
        <Button className={cn(styles.detailsButton, redButtonClass, orangeButtonClass)} onClick={() => onSelect(order.uuid)}>
          Details
        </Button>
      </div>
    </div>
  )
}

OrderRow.defaultProps = {
  order: {},
}

OrderRow.propTypes = {
  isSelected: PropTypes.bool,
  onSelect: PropTypes.func,
  order: PropTypes.object,
}

export default OrderRow
